import './App.scss';

import { Amplify } from 'aws-amplify';
import { LoadingOverlay, Snackbar } from '@finpay-development/shared-components';
import { Grid } from '@mui/material';
import jwt from 'jwt-decode';
import React, { useEffect} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BrowserRouter as Router,
  Route,
  Routes,
} from 'react-router-dom';

import { setPathName } from './admin/state/users/admin-slice';
import RouteConfiguration, { RouteConfigProps } from './route-configuration';
import AuthenticationMonitor from './security/components/authentication-monitor';
import GuardedComponent from './security/components/guarded-component';
import SessionTimeout from './security/components/session-timeout';
import { hideStatus } from './security/state/user-slice';
import { checkIfUserIsAuthenticated, logUserOut, refreshToken } from './security/state/user-thunk';
import FppHeader from './shared/components/header';
import { getConfiguration } from './shared/configuration/config-settings';
import { DecodedIdToken } from './shared/model/decodedIdToken';
import { RootState } from './shared/state/root-reducer';
import { AppDispatch } from './shared/state/store';
import {getMetaData} from './meta-data/state/meta-data-thunk';
import { CookieStorage } from 'aws-amplify/utils';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import {
  UnauthenticatedAuthRouter
} from './security/components/unauthenticated-auth-router';


// get the configuration based on current url dns name
const config = getConfiguration();

// Setup Amplify AWS User Pool Configuration for v6
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.aws_userPoolId,
      userPoolClientId: config.aws_userPoolWebClientId,
      identityPoolId: ""
    },
  },
});
//https://github.com/aws-amplify/amplify-js/issues/12629
cognitoUserPoolsTokenProvider.setKeyValueStorage(new CookieStorage({
    domain: window.location.hostname.includes('localhost') ? ".localhost" :
        (config.app_environment==='prod' ? ".finpay.net" : `.${config.app_environment}.finpay.net`),
    secure: true,
    //expire in days
    expires: 1,
    sameSite: 'none'
}));

function App() {
  const dispatch = useDispatch<AppDispatch>();

  const stateFields = {
    userIsAuthenticated: useSelector((state: RootState) => state.userContext.userIsAuthenticated),
    userRolePages: useSelector((state: RootState) => state.userContext.userProfile.userRole.userRolePages),
    subNav: useSelector((state: RootState) => state.userContext.userProfile.SubNav),
    isAccountHolderUser: useSelector((state: RootState) => state.userContext.userProfile.userRole.roleName === "Account Holder"),
    applicationStatus: useSelector((state: RootState) => state.userContext.applicationStatus),
    isLoading: useSelector((state: RootState) => state.userContext.isLoading),
    idToken: useSelector((state: RootState) => state.userContext.userProfile.tokenState.jwtIdToken),
    isSSO: useSelector((state: RootState) => state.userContext.isSSO),
    isSSOLoading: useSelector((state: RootState) => state.userContext.isSSOLoading),
    userRoleId: useSelector((state: RootState) => state.userContext.userProfile.userRole.userRoleId),
    showLogin: useSelector((state: RootState) => state.adminContext.adminUserContext.showLogin),
    pathName: useSelector((state: RootState) => state.adminContext.adminUserContext.pathName)
  }

  const {
    userIsAuthenticated,
    userRolePages,
    applicationStatus,
    isLoading,
    idToken,
    isSSO,
    isSSOLoading,
    userRoleId,
  } = stateFields;

  useEffect(() => {
    if(window.location.pathname.includes('forgot-password')){
      dispatch(logUserOut());
      dispatch(setPathName('forgot-password'))
    }
  }, []);

  const hasAccessToPage = (pageName: string) => {
    return (
      userRolePages.findIndex((page) => {
        return (
          page?.sysRolePage?.rolePageName === pageName &&
          page.isEnabled === true
        );
      }) !== -1
    );
  };

  useEffect(()=>{
    if(userIsAuthenticated && userRoleId !== 4){
      //fetching meta in case browser is refreshed
      dispatch(getMetaData());
    }
  },[dispatch, userIsAuthenticated])

  // though the token is automatically refreshed when it expires and an api call happens,
  // this function may have some performance benefits due to caching.
  useEffect(() => {
    if (idToken) {
      const tokenInfo: DecodedIdToken = jwt(idToken);
      const timeoutMs = tokenInfo.exp * 1000 - new Date().getTime() + 500;
      setTimeout(() => {
        dispatch(refreshToken());
      }, timeoutMs);
    }
  }, [idToken, dispatch]);

  const routeComponents = RouteConfiguration.map(
    (route: RouteConfigProps) => (
        <Routes>
        {route.protectedPageName ? (
          <Route
            path={route.path}
            key={route.path}
            element={
              <GuardedComponent
                exact={true}
                key={route.path}
                path={route.path}
                component={route.component}
                isAuthenticated={userIsAuthenticated}
                hasAccess={hasAccessToPage(route.protectedPageName)}
              />
            }>
          </Route>
        ) : (
          <Route
            key={route.path}
            path={route.path}
            element={<route.component/>}
          />
        )}
        </Routes>
    )
  );

  function handleCloseSnackbar() {
    dispatch(hideStatus());
  }
  // On Initial Load Check if User Already Has a Session
  const mount = () => {
    let isMounted = true;
    if (!(userIsAuthenticated && isSSO) && isMounted) {
      dispatch(checkIfUserIsAuthenticated());
    }
    return () => {
      isMounted = false;
    };
  };

  const handleLogoutClick = async () => {
    await dispatch(logUserOut());
    window.location.reload()
  };

  const shouldShowNav = () => {
    return (
      userIsAuthenticated &&
      !window.location.pathname.includes("sign-document") &&
      !window.location.pathname.includes("sign-doc")
    ); // client signing URL
  };

  useEffect(mount, [dispatch, userIsAuthenticated, isSSO])

  function toggleLoading(comp : JSX.Element){
     if (isSSOLoading) {
      return <LoadingOverlay whiteBackground={true}/>;
    }
    else{
      return comp;
    }
  }

  const isNotAuthenticated =(<UnauthenticatedAuthRouter/>);

  const isAuthenticated = (
    <>
      <AuthenticationMonitor />
      <div className="page-container">
        <FppHeader showLinks={shouldShowNav()} />
        {isLoading || isSSOLoading ? (
            <LoadingOverlay />
        ) : (
          <>
            {routeComponents}
          </>
        )}
      </div>
    </>
  );

  return (
    <main>
    <div className={"page-container"} style={{ backgroundColor: "#F5F7F9" }}>
      <Router>
        <Grid container style={{height: '100%'}}>
          {!userIsAuthenticated && (
            <>
            <Grid item xs={12}>
              <FppHeader showLinks={shouldShowNav()} />
            </Grid>
            </>
          )}
          <Grid item md={12} xs={12}>
            {toggleLoading(userIsAuthenticated ? isAuthenticated : isNotAuthenticated)}
              <Snackbar
                open={applicationStatus.showMessage}
                message={applicationStatus.message}
                type={applicationStatus.messageType}
                onClose={handleCloseSnackbar}
              />
              <SessionTimeout
                isAuthenticated={userIsAuthenticated}
                logOut={handleLogoutClick}
              />
          </Grid>
        </Grid>
      </Router>
    </div>
    </main>
  );
}
export default App;
